import { useState, useEffect, useCallback } from "react";
import { Plus, Trash2, Save, ChevronUp, ChevronDown, ShoppingBag, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "@/lib/api";

const inp = "w-full border-2 border-foreground rounded-lg px-3 py-2.5 bg-background";

export default function TakeawayEditor() {
  const [enabled, setEnabled] = useState(false);
  const [deliveryEnabled, setDeliveryEnabled] = useState(true);
  const [deliveryFee, setDeliveryFee] = useState(8);
  const [onlinePayment, setOnlinePayment] = useState(false);
  const [printerUrl, setPrinterUrl] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/takeaway");
      setEnabled(data.enabled);
      setDeliveryEnabled(data.delivery_enabled);
      setDeliveryFee(data.delivery_fee);
      setOnlinePayment(!!data.online_payment);
      setItems(data.items || []);
      const cfg = await api.get("/admin/takeaway/config");
      setPrinterUrl(cfg.data.printer_url || "");
    } catch (err) { toast.error(apiErrorMessage(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setItem = (i, key) => (e) =>
    setItems((arr) => arr.map((x, j) => (j === i ? { ...x, [key]: e.target.value } : x)));

  const move = (i, dir) =>
    setItems((arr) => {
      const j = i + dir;
      if (j < 0 || j >= arr.length) return arr;
      const next = [...arr];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  const addItem = () => setItems((arr) => [...arr, { name: "", desc: "", price: 0 }]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled, delivery_enabled: deliveryEnabled, delivery_fee: Number(deliveryFee) || 0,
        online_payment: onlinePayment,
        printer_url: printerUrl.trim(),
        items: items
          .filter((it) => (it.name || "").trim())
          .map((it, i) => ({ id: it.id, name: it.name.trim(), desc: (it.desc || "").trim(), price: Number(it.price) || 0, order: i })),
      };
      await api.put("/admin/takeaway", payload);
      toast.success("Takeaway saved");
      load();
    } catch (err) { toast.error(apiErrorMessage(err)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="py-20 text-center"><Loader2 className="animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-8 max-w-3xl">
      <div className="bg-background border-2 border-foreground rounded-2xl p-6 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h3 className="font-head font-extrabold text-lg flex items-center gap-2"><ShoppingBag size={18} /> Takeaway availability</h3>
            <p className="text-sm text-foreground/60">Turn this on to show the chalkboard takeaway menu on your website. Turn it off any time takeaway isn't available.</p>
          </div>
          <button
            role="switch" aria-checked={enabled} data-testid="takeaway-enabled-toggle"
            onClick={() => setEnabled((v) => !v)}
            className={`relative w-14 h-8 rounded-full border-2 border-foreground shrink-0 transition-colors ${enabled ? "bg-secondary" : "bg-muted"}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-foreground transition-transform ${enabled ? "translate-x-6" : ""}`} />
          </button>
        </div>

        <div className="flex items-center justify-between gap-4 border-t-2 border-foreground/10 pt-4">
          <div>
            <p className="font-head font-bold">Offer delivery</p>
            <p className="text-sm text-foreground/60">Show a pickup / delivery choice at checkout.</p>
          </div>
          <button
            role="switch" aria-checked={deliveryEnabled} data-testid="takeaway-delivery-toggle"
            onClick={() => setDeliveryEnabled((v) => !v)}
            className={`relative w-14 h-8 rounded-full border-2 border-foreground shrink-0 transition-colors ${deliveryEnabled ? "bg-secondary" : "bg-muted"}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-foreground transition-transform ${deliveryEnabled ? "translate-x-6" : ""}`} />
          </button>
        </div>

        {deliveryEnabled && (
          <label className="flex items-center gap-3 border-t-2 border-foreground/10 pt-4">
            <span className="font-head font-bold">Delivery fee $</span>
            <input type="number" min="0" step="0.5" className="w-28 border-2 border-foreground rounded-lg px-3 py-2 bg-background" value={deliveryFee} onChange={(e) => setDeliveryFee(e.target.value)} data-testid="takeaway-fee-input" />
          </label>
        )}

        <div className="flex items-center justify-between gap-4 border-t-2 border-foreground/10 pt-4">
          <div>
            <p className="font-head font-bold">Take card payment online</p>
            <p className="text-sm text-foreground/60">Off = customers order online and pay in person at collection (via your till). On = customers pay by card at checkout.</p>
          </div>
          <button
            role="switch" aria-checked={onlinePayment} data-testid="takeaway-online-payment-toggle"
            onClick={() => setOnlinePayment((v) => !v)}
            className={`relative w-14 h-8 rounded-full border-2 border-foreground shrink-0 transition-colors ${onlinePayment ? "bg-secondary" : "bg-muted"}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-foreground transition-transform ${onlinePayment ? "translate-x-6" : ""}`} />
          </button>
        </div>

        <div className="border-t-2 border-foreground/10 pt-4">
          <p className="font-head font-bold">Kitchen printer (Star WebPRNT)</p>
          <p className="text-sm text-foreground/60 mb-2">Enter your Star printer's IP address (e.g. <code>192.168.1.50</code>). Leave blank to keep using the browser print dialog. Silent auto-printing needs the Dashboard open inside the free <b>Star WebPRNT Browser</b> app on the counter iPad, on the same network as the printer.</p>
          <input
            value={printerUrl} onChange={(e) => setPrinterUrl(e.target.value)}
            data-testid="takeaway-printer-input"
            placeholder="192.168.1.50"
            className="w-full sm:w-80 border-2 border-foreground rounded-lg px-3 py-2.5 bg-background font-mono-plex"
          />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-head font-extrabold text-lg">Takeaway menu items</h3>
          <button onClick={addItem} data-testid="takeaway-add-item" className="flex items-center gap-2 font-head font-bold text-sm border-2 border-foreground rounded-full px-4 py-2 hover:bg-muted"><Plus size={15} /> Add item</button>
        </div>
        <p className="text-sm text-foreground/60 mb-4">These show on the chalkboard, in the order below. Reorder with the arrows.</p>
        <div className="space-y-3">
          {items.map((it, i) => (
            <div key={it.id || i} data-testid={`takeaway-item-${i}`} className="flex gap-2 items-start border-2 border-foreground/15 rounded-xl p-3">
              <div className="flex flex-col justify-center shrink-0 pt-1">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`takeaway-up-${i}`} aria-label="Move up" className="px-1.5 py-0.5 border-2 border-foreground rounded-t-md hover:bg-muted disabled:opacity-30"><ChevronUp size={14} /></button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} data-testid={`takeaway-down-${i}`} aria-label="Move down" className="px-1.5 py-0.5 border-2 border-t-0 border-foreground rounded-b-md hover:bg-muted disabled:opacity-30"><ChevronDown size={14} /></button>
              </div>
              <div className="flex-1 space-y-2">
                <div className="flex gap-2">
                  <input className={inp} placeholder="Item name (e.g. Fish & Chips)" value={it.name} onChange={setItem(i, "name")} data-testid={`takeaway-name-${i}`} />
                  <div className="flex items-center gap-1 shrink-0">
                    <span className="font-head font-bold">$</span>
                    <input type="number" min="0" step="0.5" className="w-24 border-2 border-foreground rounded-lg px-2 py-2.5 bg-background" placeholder="0" value={it.price} onChange={setItem(i, "price")} data-testid={`takeaway-price-${i}`} />
                  </div>
                </div>
                <input className={inp} placeholder="Short description (optional)" value={it.desc} onChange={setItem(i, "desc")} data-testid={`takeaway-desc-${i}`} />
              </div>
              <button onClick={() => setItems((arr) => arr.filter((_, j) => j !== i))} data-testid={`takeaway-del-${i}`} className="p-3 border-2 border-destructive text-destructive rounded-lg hover:bg-destructive hover:text-destructive-foreground shrink-0"><Trash2 size={16} /></button>
            </div>
          ))}
          {!items.length && <p className="text-foreground/50 text-sm py-6 text-center border-2 border-dashed border-foreground/20 rounded-xl">No items yet — add your first takeaway dish.</p>}
        </div>
      </div>

      <button onClick={save} disabled={saving} data-testid="takeaway-save" className="flex items-center gap-2 font-head font-extrabold bg-secondary text-secondary-foreground border-2 border-foreground rounded-full px-6 py-3 hover:translate-y-[2px] transition-transform disabled:opacity-60">
        {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />} Save takeaway
      </button>
    </div>
  );
}
